import { useSuspenseQuery } from "@tanstack/react-query"
import styled from "@emotion/styled"
import { FaCaretDown } from "@react-icons/all-files/fa/FaCaretDown"
import { Value, htmlFormatTags } from "@stringtale/react"
import SwitchGroupMenu from "app/groups/components/SwitchGroupMenu"
import { doesOrganizationHaveGroupAccess } from "app/organizations/utils/doesOrganizationHaveGroupAccess"
import { ComponentProps, ReactNode } from "react"
import { DARK_BLUE_COLOR, PX14 } from "theme/consts"
import * as Container from "ui/Container"
import { useCurrentGroup } from "../hooks/useCurrentGroup"
import { useCurrentOrganization } from "../hooks/useCurrentOrganization"
import { useCurrentUser } from "../hooks/useCurrentUser"
import BreadcrumbItem from "./BreadcrumbItem"
import { trpc } from "app/trpc"

const Root = styled(Container.Root)<{ backgroundColor?: string }>`
  background-color: ${(p) => p.backgroundColor || "white"};
  /* height: 40px; */
`

const Content = styled(Container.Content)`
  display: flex;
  align-items: center;
  height: 40px;
  justify-content: space-between;
`

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`
const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`

const BreadcrumbItems = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`

const BreadcrumbGroup = styled.span`
  color: ${DARK_BLUE_COLOR};
  font-size: ${PX14};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 2px;
`

type Props = {
  items: Array<ReactNode>
  right?: ReactNode
} & ComponentProps<typeof Root>

function Breadcrumb({ items, right, ...props }: Props) {
  const currentGroup = useCurrentGroup()
  const organization = useCurrentOrganization()
  const { data: groups } = useSuspenseQuery(
    trpc.auth.getGroupsInSchool.queryOptions()
  )
  const currentUser = useCurrentUser()

  const organizationAlreadyHasGroups = groups.length > 0
  // If you already have groups, even if the organization doesn't have group access, you should
  // still be able to switch groups and thus show the group picker
  // if you don't have groups AND the organization doesn't have group access, you should not be able to switch groups
  const cantHaveGroups =
    !organizationAlreadyHasGroups ||
    !doesOrganizationHaveGroupAccess(organization, currentUser)

  return (
    <Root {...props}>
      <Content>
        <Left>
          {organization && currentGroup && groups && !cantHaveGroups && (
            <SwitchGroupMenu
              currentGroup={currentGroup}
              trigger={
                currentGroup ? (
                  <BreadcrumbGroup style={{ cursor: "pointer" }}>
                    {currentGroup.displayTitle}
                    <FaCaretDown />
                  </BreadcrumbGroup>
                ) : (
                  <BreadcrumbGroup style={{ cursor: "pointer" }}>
                    <Value name="apps.web.src.core.components.breadcrumb.geen_actieve_groep">
                      Geen actieve groep
                    </Value>
                    <FaCaretDown />
                  </BreadcrumbGroup>
                )
              }
              groups={groups}
            />
          )}
          <BreadcrumbItems>
            {organization && groups && !cantHaveGroups && (
              <span style={{ opacity: 0.5 }}>| </span>
            )}
            {items.map((item, i) => (
              <>
                {i > 0 && <span style={{ opacity: 0.5 }}>&#62;</span>}
                {item}
              </>
            ))}
            {organization?.trialAccount && organization.trialEndDate && (
              <>
                <span style={{ opacity: 0.5 }}>|</span>
                <BreadcrumbItem isActive>
                  <Value
                    name="apps.web.src.core.components.breadcrumb.looptijd_proefabonnement_tot"
                    format={{
                      ...htmlFormatTags,
                      trialEndDate: organization.trialEndDate,
                    }}
                    locales="nl-NL"
                  >
                    {`Looptijd proefabonnement tot: <b>{trialEndDate, date, dd-MM-yyyy}</b>`}
                  </Value>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbItems>
        </Left>
        {right && <Right>{right}</Right>}
      </Content>
    </Root>
  )
}

Breadcrumb.Item = BreadcrumbItem

export default Breadcrumb
